import { useCallback, useEffect, useState } from 'react';
import { useTenant } from '@/contexts/TenantContext';
import { isExpaiApiEnabled } from '@/integrations/api/config';
import { fetchTenantModules } from '@/integrations/api/platform_api';
import { PLATFORM_MODULE_KEYS } from '@/integrations/api/platform_modules';

export interface AppModule {
  key: string;
  label: string;
  enabled: boolean;
  alwaysOn?: boolean;
}

const PRIVILEGED_ROLES = new Set(['admin', 'owner']);
const ADMIN_FORCE_ENABLED_MODULES = new Set(['mlops', 'quality']);

const DEFAULT_MODULES: AppModule[] = [
  { key: 'dashboard', label: 'Dashboard', enabled: true, alwaysOn: true },
  { key: 'admin', label: 'Administración', enabled: true, alwaysOn: true },
  { key: 'production', label: 'Producción', enabled: true },
  { key: 'quality', label: 'Calidad', enabled: true },
  { key: 'logistics', label: 'Logística', enabled: true },
  { key: 'maintenance', label: 'Mantenimiento', enabled: true },
  { key: 'reports', label: 'Reportes', enabled: true },
  { key: 'backoffice', label: 'BackOffice', enabled: true },
  { key: 'mlops', label: 'MLOps', enabled: true },
];

const PLATFORM_TO_UI: Record<string, string> = {
  mes: 'production',
  mlops: 'mlops',
  quality: 'quality',
  reports: 'reports',
  agent: 'agent',
  files: 'files',
};

interface UseModulesResult {
  modules: AppModule[];
  isEnabled: (key: string) => boolean;
  toggle: (key: string) => Promise<void>;
  isLoading: boolean;
}

/**
 * Module catalog for onboarding and admin UI.
 * Reads tenant modules from the Adonis API when enabled.
 */
export function useModules(): UseModulesResult {
  const { currentOrg } = useTenant();
  const [modules, setModules] = useState<AppModule[]>(DEFAULT_MODULES);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      let next = DEFAULT_MODULES;
      if (currentOrg && isExpaiApiEnabled()) {
        try {
          const { modules: rows } = await fetchTenantModules(currentOrg.id);
          const map = new Map(rows.map((r) => [r.module_key, r.enabled]));
          next = DEFAULT_MODULES.map((m) => {
            const platformKey = Object.entries(PLATFORM_TO_UI).find(([, ui]) => ui === m.key)?.[0];
            if (!platformKey || !PLATFORM_MODULE_KEYS.includes(platformKey as typeof PLATFORM_MODULE_KEYS[number])) {
              return m;
            }
            return {
              ...m,
              enabled: m.alwaysOn ? true : (map.get(platformKey) ?? true),
            };
          });
        } catch {
          // keep defaults
        }
      }
      if (!cancelled) {
        // Keep critical advanced modules visible for privileged users
        // even if tenant module rows come desynchronized in local dev.
        if (currentOrg && PRIVILEGED_ROLES.has(currentOrg.role)) {
          next = next.map((m) =>
            ADMIN_FORCE_ENABLED_MODULES.has(m.key)
              ? {
                  ...m,
                  enabled: true,
                }
              : m,
          );
        }

        setModules(next);
        setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentOrg]);

  const isEnabled = useCallback(
    (key: string) => {
      const m = modules.find((x) => x.key === key);
      if (!m) return true;
      return m.alwaysOn ? true : m.enabled;
    },
    [modules],
  );

  const toggle = useCallback(
    async (key: string) => {
      setModules((prev) =>
        prev.map((m) => (m.key === key && !m.alwaysOn ? { ...m, enabled: !m.enabled } : m)),
      );
    },
    [],
  );

  return { modules, isEnabled, toggle, isLoading };
}
